<script setup lang="ts">
import type { LoginComponentModel } from '@su4r-frontend/api';

const { description, forgotPassword, register, title } = defineProps<LoginComponentModel>();

const { signIn } = useAuth();
const localeRoute = useLocaleRoute();
const route = useRoute();
const { t } = useI18n();

const loginForm = ref({
    email: '',
    password: '',
});

const generalError = ref<string | null>(null);
const formErrors = ref({
    email: '',
    password: '',
});

function clearErrorMessages(): void {
    formErrors.value.email = '';
    formErrors.value.password = '';
}

function isFormValid(e: Event): boolean {
    if (!e.target) return false;
    const formElement = e.target as HTMLFormElement;

    Array.from(formElement).forEach((el) => {
        const element = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

        if (element.name === 'email') {
            if (element.validity.valueMissing) {
                formErrors.value.email = t('form.formErrorRequired');
            }
        }

        if (element.name === 'password') {
            if (element.validity.valueMissing) {
                formErrors.value.password = t('form.formErrorRequired');
            }
        }
    });

    return Object.values(formErrors.value).every((error) => error === '');
}

const { data: authData } = useAuth();

async function handleLogin(): Promise<void> {
    const loginResponse = await signIn('credentials', {
        email: loginForm.value.email,
        password: loginForm.value.password,
        redirect: false,
    });

    // TODO: Replace with localized link from setup data
    const defaultRedirectUrl =
        authData.value?.user?.role === 'patient'
            ? `/dashboard/${authData.value?.user.name}/current`
            : '/dashboard';

    const redirectUrl = (route.query?.redirectUrl as string) || defaultRedirectUrl;

    if (loginResponse?.error) {
        if (loginResponse?.error === 'invalid_grant') {
            generalError.value = t('login.invalidCredentials');
        } else {
            generalError.value = t('error.unknownError');
        }
    }

    if (!loginResponse?.error) {
        await navigateTo(decodeURIComponent(redirectUrl));
    }
}

async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    clearErrorMessages();
    if (isFormValid(e)) {
        await handleLogin();
    }
}
</script>

<template>
    <section class="container grid grid-cols-12">
        <div class="col-span-12 pt-2 lg:col-span-8 lg:col-start-3">
            <form class="tile max-w-full bg-white" novalidate @submit.prevent="handleSubmit">
                <div class="grid gap-4 lg:gap-6">
                    <div>
                        <h4 class="text-xl">{{ title }}</h4>
                        <p class="pink">{{ description }}</p>
                    </div>
                    <hr class="text-black/10" />
                    <div class="flex flex-col flex-wrap gap-2">
                        <div class="flex flex-col gap-2">
                            <h6 class="font-body w-full font-semibold">
                                {{ $t('form.personalDetails') }}
                            </h6>
                            <InlineAlert v-if="generalError" class="my-2">
                                {{ generalError }}
                            </InlineAlert>
                        </div>

                        <div class="flex flex-col gap-4">
                            <Text
                                v-model="loginForm.email"
                                type="email"
                                name="email"
                                :error-message="formErrors.email"
                                :validation-type="formErrors.email ? 'error' : 'none'"
                                :required="true"
                                :label="$t('form.emailAddressLabel')" />
                            <Text
                                v-model="loginForm.password"
                                type="password"
                                :error-message="formErrors.password"
                                :validation-type="formErrors.password ? 'error' : 'none'"
                                name="password"
                                :required="true"
                                :label="$t('form.passwordLabel')" />
                        </div>
                    </div>
                    <div class="flex flex-col items-center gap-2 lg:items-start">
                        <button type="submit" class="btn-primary-a">
                            {{ $t('common.login') }}
                        </button>
                        <NuxtLink
                            class="btn-tertiary-a inline-button"
                            :to="localeRoute(forgotPassword?.url ?? '')"
                            :target="forgotPassword?.openInNewTab ? '_blank' : '_self'">
                            {{ forgotPassword?.title }}
                        </NuxtLink>
                        <NuxtLink
                            class="btn-tertiary-a inline-button"
                            :to="localeRoute(register?.url ?? '')"
                            :target="register?.openInNewTab ? '_blank' : '_self'">
                            {{ register?.title }}
                        </NuxtLink>
                    </div>
                </div>
            </form>
        </div>
    </section>
</template>
